import { useEffect, useRef, useState } from "react";
import { FileText, X } from "lucide-react";

import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

export default function TermsPage() {
  const [termsText, setTermsText] = useState("");
  const [, setVersion] = useState("");
  const [loading, setLoading] = useState(true);
  const { getLegalDocuments } = useAuth();
  const toast = useToast();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    getLegalDocuments()
      .then((docs) => {
        if (cancelled) return;
        setTermsText(docs.terms_text);
        setVersion(docs.terms_version);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        toast.error(error instanceof Error ? error.message : "Unable to load terms");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getLegalDocuments, toast]);

  const paragraphs = termsText
    ? termsText.split(/\n{2,}/).filter(Boolean)
    : [];

  return (
    <main className="flex-1 bg-muted/40">
      {/* Hero */}
      <div className="bg-background border-b border-border/80">
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex items-start gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
              <FileText className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Terms and Conditions</h1>
              <p className="mt-1.5 text-base text-muted-foreground">
                Please read carefully before accepting.
              </p>
            </div>
            {/* Close button if opened in a new tab */}
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5"
              onClick={() => window.close()}
            >
              <X className="h-4 w-4" />
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <div
          ref={contentRef}
          className="rounded-2xl border border-border/80 bg-background shadow-sm overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="divide-y divide-border/60">

              {/* Rendered text */}
              <div className="px-6 py-8 sm:px-10">
                {paragraphs.length > 0 ? (
                  <div className="space-y-5 text-sm leading-7 text-foreground [&_a]:pointer-events-none [&_a]:text-inherit [&_a]:no-underline">
                    {paragraphs.map((para, i) => {
                      const isHeading = /^\d+\.\s/.test(para) && para.length < 50;
                      if (isHeading) {
                        return (
                          <h2 key={i} className="mt-8 first:mt-0 text-base font-semibold text-foreground">
                            {para}
                          </h2>
                        );
                      }
                      return (
                        <p key={i} className="text-muted-foreground">
                          {para}
                        </p>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No terms content available.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            You may close this window and return to complete your onboarding.
          </p>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.close()}>
            <X className="h-4 w-4" />
            Close Window
          </Button>
        </div>
      </div>
    </main>
  );
}
